//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { i18n } from "../i18n/common.i18n"

import { Enums } from "../../../models/common/enums"
import { IMRBaseControlDirectiveController } from '../base'

// Options

export type ChangeStatusSelectItem = {
    caption: string,
    value: any
}

export type ChangeStatusOptions = {
    readonly?: boolean

    noDataText?: string

    noteDataType: Enums.DataType
    noteLabel?: string
    noteValue?: any

    selectCaptionFieldName?: string

    // TypeScript dictionary instead of Dictionary<T> type because of the possibility of defining custom property names (value, caption) of dataSource elements.
    selectDataSource: { [index: string]: any }[]
    selectDataType: Enums.DataType
    selectLabel?: string
    selectPlaceholder?: string
    selectValue?: any
    selectValueFieldName?: string

    onSelectValueChanged: (value: ChangeStatusSelectItem, dataType: Enums.DataType) => void
    onNoteValueChanged: (value: string, dataType: Enums.DataType) => void
}

// Options End


export interface ChangeStatusScope extends ng.IScope {
    id: number
    options: ChangeStatusOptions
    isLoading: boolean
    isInitializing: boolean

    formOptions: DevExpress.ui.dxFormOptions
    dropDownOptions: any
}

export class ChangeStatusController extends IMRBaseControlDirectiveController {

    // Localization Consts

    private localizationText = class {
        public static get NoData() { return 'NODATA' }
        public static get Note() { return 'NOTE' }
        public static get Select() { return 'SELECT' }
    }

    // Localization Consts End


    // Consts

    private get DEFAULT_SELECT_VALUE_FIELD_NAME(): string { return 'value' }
    private get DEFAULT_SELECT_CAPTION_FIELD_NAME(): string { return 'caption' }
    private get DEFAULT_DROP_DOWN_WIDTH(): string { return '300' }

    private get LOCALIZATION_COLLECTION_NAME(): string { return 'Common' }

    // Consts End


    // Members

    private dxDropDown: any

    // Members End


    constructor(private $scope: ChangeStatusScope) {
        super()

        $scope.isInitializing = true

        this.onInitalize($scope.options)
    }


    // Lifecycle 

    private onInitalize(options: ChangeStatusOptions): void {
        if (this.$scope.isInitializing) {
            this.validateOptions(options)

            this.$scope.isInitializing = false
            this.$scope.options = options

            this.setDropDownOptions()
        }
    }

    // Lifecycle End


    // Events

    private onNoteValueChanged(e: any): void {
        this.$scope.options.onNoteValueChanged && this.$scope.options.onNoteValueChanged(e.value, this.$scope.options.noteDataType)
    }

    private onSelectValueChanged(e: any): void {
        this.dxDropDown.option('value', e.selectedItem[this.getValueExpr()])

        const returnValue: ChangeStatusSelectItem = {
            value: e.selectedItem[this.getValueExpr()],
            caption: e.selectedItem[this.getDisplayExpr()]
        }

        this.$scope.options.onSelectValueChanged && this.$scope.options.onSelectValueChanged(returnValue, this.$scope.options.selectDataType)
    }

    // Events End


    // Setup

    private getDisplayExpr(): string {
        return this.$scope.options.selectCaptionFieldName || this.DEFAULT_SELECT_CAPTION_FIELD_NAME
    }

    private getFormOptions(): DevExpress.ui.dxFormOptions {
        const formItems: DevExpress.ui.dxFormSimpleItem[] = [{
            dataField: 'Type',
            editorOptions: {
                dataSource: this.$scope.options.selectDataSource,
                displayExpr: this.getDisplayExpr(),
                placeholder: this.$scope.options.selectPlaceholder || this.getLocalizedText(this.localizationText.Select),
                valueExpr: this.getValueExpr(),

                onSelectionChanged: (e: any) => this.onSelectValueChanged(e),
            },
            editorType: 'dxSelectBox',
            label: {
                location: 'top',
                text: this.$scope.options.selectLabel || this.getLocalizedText(this.localizationText.Select),
            },
        }, {
            dataField: 'Note',
            editorOptions: {
                autoResizeEnabled: true,
                height: 200,
                minHeight: 200,
                value: this.$scope.options.noteValue,

                onValueChanged: (e: any) => this.onNoteValueChanged(e),
            },
            editorType: 'dxTextArea',
            label: {
                location: 'top',
                text: this.$scope.options.noteLabel || this.getLocalizedText(this.localizationText.Note),
            },
        }]

        return {
            formData: {
                Note: this.$scope.options.noteValue,
                Type: this.$scope.options.selectValue,
            },
            items: formItems
        }
    }

    private getLocalizedText(text: string): string {
        return i18n(this.LOCALIZATION_COLLECTION_NAME, text)
    }

    private getValueExpr(): string {
        return this.$scope.options.selectValueFieldName || this.DEFAULT_SELECT_VALUE_FIELD_NAME
    }

    private setDropDownOptions(): void {
        this.$scope.dropDownOptions = {
            dataSource: this.$scope.options.selectDataSource,
            displayExpr: this.getDisplayExpr(),
            dropDownOptions: {
                width: this.DEFAULT_DROP_DOWN_WIDTH
            },
            placeholder: this.$scope.options.selectPlaceholder || this.getLocalizedText(this.localizationText.Select),
            noDataText: this.$scope.options.noDataText || this.getLocalizedText(this.localizationText.NoData),
            readOnly: this.$scope.options.readonly,
            value: this.$scope.options.selectValue,
            valueExpr: this.getValueExpr(),

            contentTemplate: (e: any) => $('<div>').dxForm(this.getFormOptions()),
            onInitialized: (e: any) => this.dxDropDown = e.component,
        }
    }

    private validateOptions(options: ChangeStatusOptions) {
        if (options == null) {
            throw new Error('The options object cannot be undefined.')
        }
    }

    // Setup End
}